<template>
  <div class="grid grid-cols-2 gap-5">
    <v-overlay
      v-model="isUploading"
      class="align-center justify-center"
      persistent
      scrim="rgba(0, 0, 0, 0.65)"
    >
      <v-card class="pa-8" max-width="420" rounded="lg">
        <div class="flex flex-col items-center gap-4 text-center">
          <v-progress-circular color="primary" indeterminate size="56" width="4"/>
          <div class="text-h6">Uploading your document</div>
          <p class="text-body-2 text-medium-emphasis mb-0">
            Sending your file securely. This can take a little while—please keep this page open.
          </p>
        </div>
      </v-card>
    </v-overlay>

    <v-card :disabled="isUploading" class="mb-2">
      <v-card-text>
        <div class="my-5">
          Upload your document here. In the next step you will be able to add variable placeholders to your documents.
        </div>
        <div v-if="!documentsUploadedSuccess" class="dragBox" v-bind="getRootProps()">
          <input v-bind="getInputProps()"/>
          <p v-if="isDragActive">Drop the files here ...</p>
          <p v-else>Drag and drop your file here, or click to select files</p>
        </div>

        <div v-else>
          <div class="h-100 row align-items-center">
            <div class="col text-center">
              <v-img height="200" src="/images/illustrations/my_files.svg"/>
              <h2 class="newCustomer__header">Success</h2>
              <p>Click next to add placeholders to your template.</p>
            </div>
          </div>
        </div>

        <div v-if="rejectedReasons.length" class="mt-5">
          <v-alert density="comfortable" type="error">
            <ul>
              <li v-for="(reason, index) in rejectedReasons" :key="index">{{ reason }}</li>
            </ul>
          </v-alert>
        </div>

      </v-card-text>
      <v-card-actions>
        <v-btn :to="{name: 'documents.index'}" size="small" variant="text">back</v-btn>
        <v-spacer/>
        <v-btn :disabled="!documentsUploadedSuccess" :to="{name: 'documentsv2.fields', params: { uuid: documentUuid}}" color="primary">
          {{ $t('next') }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </div>
</template>

<script lang="ts" setup>
import {useRoute} from 'vue-router'
import type {AxiosInstance} from 'axios';
import axios from 'axios';
import type {FileRejectReason} from 'vue3-dropzone'
import {useDropzone} from 'vue3-dropzone';
import {ref} from 'vue';

interface GetUploadUrlParams {
  document_uuid: string;
  actor: string;
  extension: string;
  mime: string;
}

interface UploadUrlResponse {
  uploadUrl: string;
}

const route = useRoute();
const thePhotosUploading = ref<unknown[]>([]);
const filesForS3 = ref<File[]>([]);
const templateDocs = ref<Record<string, unknown>>({});
const documentsUploadedSuccess = ref(false);
const isUploading = ref(false);
const rejectedReasons = ref<string[]>([]);
const uploadUrls = ref<Array<string | null>>([]);

const documentUuid = route.params.uuid as string;


const getUploadUrl = async (file: File, uuid: string) => {
  // Extract the file extension and fallback if necessary
  const extension = file.name.split('.').pop() || 'txt';

  // Prepare the payload for the API
  const payload: GetUploadUrlParams = {
    document_uuid: uuid,
    actor: 'host',
    extension,
    mime: file.type || 'application/octet-stream',
  };

  try {
    const response = await fetch('https://tljgib4tw6.execute-api.us-west-1.amazonaws.com/default/TenantFileSignedUploadUrl', {
      method: 'POST',
      headers: {'Content-Type': 'application/json'},
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      console.error(`Failed to fetch upload URL. Status: ${response.status} - ${response.statusText}`);
      return null;
    }

    const {uploadUrl}: UploadUrlResponse = await response.json();
    return uploadUrl;
  } catch (error) {
    console.error('Error getting upload URL', error);
    return null;
  }
};

const uploadS3Init = async () => {
  thePhotosUploading.value = [];
  uploadUrls.value = await Promise.all(filesForS3.value.map((file) => getUploadUrl(file, documentUuid)));

  const pairs = filesForS3.value.flatMap((file, index) => {
    const url = uploadUrls.value[index];
    return url ? [{url, file}] : [];
  });

  await uploadS3(pairs);
};

const createAwsAxiosInstance = () => {
  return axios.create({
    transformRequest: (data, headers) => {
      if (headers?.common?.Authorization) {
        delete headers.common.Authorization;
      }
      return data;
    },
  });
};

const uploadS3 = async (uploads: Array<{ url: string; file: File }>) => {
  const awsAxios = createAwsAxiosInstance();
  await Promise.all(uploads.map((upload, i) => uploadFileToS3(awsAxios, upload.url, upload.file, i)));
};

const uploadFileToS3 = async (awsAxios: AxiosInstance, filePath: string, file: File, i: number) => {
  console.dir('initializeUploadData', i);


  try {
    console.dir(filePath);
    console.dir(file);
    await awsAxios.put(filePath, file, {
      onUploadProgress: event => {
        console.dir(event);
      },
      headers: {'Content-Type': file.type},
    });
  } catch (error) {
    console.error('Error uploading file', error);
  } finally {
    console.dir('finally');
  }
};


async function onDrop(acceptedFiles: File[], rejectReasons: FileRejectReason[]) {
  rejectedReasons.value = [];
  console.log('acceptedFiles', acceptedFiles)
  console.log('rejectReasons', rejectReasons)

  if (rejectReasons.length > 0) {
    rejectReasons.forEach(reason => {
      const firstError = reason?.errors?.[0];
      const message = (firstError && typeof firstError === 'object' && 'message' in firstError)
        ? String((firstError as { message: unknown }).message)
        : undefined;

      if (message && !rejectedReasons.value.includes(message)) {
        rejectedReasons.value.push(message);
      }
    });
  }

  filesForS3.value = acceptedFiles;
  if (acceptedFiles.length === 0) {
    return;
  }

  isUploading.value = true;
  try {
    await uploadS3Init();
    await uploadDocument();
  } finally {
    isUploading.value = false;
  }
}

const uploadDocument = async () => {
  if (uploadUrls.value.length > 0) {

    const uploadUrlsClean = uploadUrls.value
      .filter((url): url is string => typeof url === 'string')
      .map((url) => url.split('?')[0]);

    if (!uploadUrlsClean[0] || !filesForS3.value[0]) {
      return;
    }

    const formData = new FormData();
    formData.append('file_path', uploadUrlsClean[0]);
    formData.append('original_name', filesForS3.value[0].name);
    try {
      const response = await axios.post(`/v1/templates/${documentUuid}/documents/uploaded`, formData);
      console.dir(response);
      documentsUploadedSuccess.value = true;
      templateDocs.value = response.data.templateDocs;
    } catch (error) {
      console.dir(error);
    }
  }
};


const {getRootProps, getInputProps, isDragActive,} = useDropzone({
  onDrop,
  maxSize: 10000000, // 10MB
  maxFiles: 1,
  accept: 'application/pdf',
});
</script>

<style>
.fw-file-drop {
  position: relative;
  border: 3px dashed #a7d2b8 !important;
  border-radius: 10px;
  box-shadow: 0 0 25px -20px #333;
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-start;
  align-items: flex-start;
  margin: 0 0 20px;
  overflow: auto;
  cursor: pointer !important;
  height: 200px;
  padding-top: 10px;
}

.fw-file-drop:hover {
  border: 3px dashed #5b7365 !important;
}

.fw-file-drop__inner {
  display: block;
  position: relative;
  width: 100%;
  height: 100%;
  margin: auto;
  overflow: auto;
}

.fw-file-drop input {
  width: 100%;
  opacity: 0;
  height: 100%;
  left: 0;
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
}

.fw-file-drop svg {
  margin: auto
}

.fw-file-drop label {
  width: 100%;
  font-size: 22px;
  font-weight: 400;

  display: inline-block;
  max-width: 100%;
  margin-bottom: 5px;
}

.dragBox {
  width: 100%;
  margin: 0 auto;
  position: relative;
  text-align: center;
  font-weight: bold;
  line-height: 95px;
  color: #999;
  border: 2px dashed rgba(var(--v-border-color), var(--v-border-opacity));
  display: inline-block;
  transition: transform 0.3s;
  border-radius: 10px;
}

.dragBox.active {
  border: 2px dashed #5a81e0;
}

.dragBox input[type="file"] {
  position: absolute;
  height: 100%;
  width: 100%;
  opacity: 0;
  top: 0;
  left: 0;
}
</style>
